import React, { useState, useEffect } from 'react';
import { scrollAppToTop } from '../../utils/scrollAppToTop.ts';
import { ChevronLeft, Home, Menu, CheckCircle, AlertCircle, BookOpen, Calculator, ChevronRight, Lightbulb, HelpCircle, Sparkles } from 'lucide-react';
import { GradientText, InsightCard, Button, IconTile } from '../../components/ui/index.tsx';
import { ACAT_LEVEL_DATA } from '../../../../shared/src/calculation/constants/acatLevelData.ts';
import ProgramOverviewSection from './ProgramOverviewSection.tsx';
import { ChspComparisonModal } from '../../components/care/EntryLevelSupportExplainer.tsx';
import { MyAgedCareApplyLink } from '../../components/branding/index.tsx';
import ContributionsSection from './ContributionsSection.tsx';
import StrategiesSection from './StrategiesSection.tsx';
import ServiceBuilderSection from './ServiceBuilderSection.tsx';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import { HOME_CARE_STRATEGIES_KEY, HOME_CARE_STRATEGIES_UPDATED_EVENT } from '../../strategy/careCompanionStrategyEvents.ts';
import SahHub, { type SahHubIntent as HubLink } from './SahHub.tsx';
import { householdVoice } from '../../utils/householdWords.ts';
import { chosenSahPlanFromSession, setSahHubIntent, SAH_START_FRESH_KEY, type ChosenSahPlan } from './sahChosenPlan.ts';
import { getServicePlanWithSource, setCurrentServicePlan, clearNoServicePlanChoice } from '../../../ServicePlanUtils.ts';
import SahStep1, { type SahServicePlan } from './SahStep1.tsx';
import SahStep2 from './SahStep2.tsx';
import { sahPricedServicePlan } from './sahEngineContext.ts';
import { computeServiceCostsForPerson } from './serviceBuilderCosts.ts';
import { SUPPORT_AT_HOME_SERVICES } from './supportAtHomeServices.tsx';
import { servicePlanDistribution } from './servicePlanBasis.ts';
import type { SavedStrategy } from '../../strategy/StrategyIntegrationUtils.ts';
import { openSupportAtHomeDigitalSOAAttachment } from './SupportAtHomeDigitalSOAAttachment.tsx';

type SupportAtHomeSection = 'hub' | 'overview' | 'contributions' | 'strategies' | 'builder' | 'letter';

function readSessionSaved(): SavedStrategy[] {
    try {
        const raw = typeof window !== 'undefined' ? window.sessionStorage.getItem(HOME_CARE_STRATEGIES_KEY) : null;
        const list = raw ? JSON.parse(raw) : [];
        return Array.isArray(list) ? list : [];
    } catch {
        return [];
    }
}

/**
 * THE ONE ORDER this module is walked in. Tabs, Previous/Next and every label read from it.
 *
 * NEED BEFORE MONEY — the Service Builder comes before Optimise (owner direction, 2026-08-27).
 * Both orders "work", because the two are only weakly coupled and asymmetrically so: optimising
 * moves assets and income, which sets the contribution RATES, and those come from the means
 * assessment rather than from which services are chosen. The Service Builder sets the BUDGET those
 * rates apply to. Out-of-pocket is budget x rate.
 *
 * So optimising first never gave wrong ADVICE — whichever strategy wins on the illustrative package
 * still wins on the real one. It gave provisional DOLLARS at the moment a client is most
 * impressionable, and nobody re-checks "your share falls to $3,929 a year" after building their
 * services. It is also the right advice order: what care someone needs is a clinical question, not
 * a financial one.
 *
 * ONE ARRAY, because there were two. The tabs listed the order and the bottom Next/Back buttons
 * carried their own hardcoded copy of it, so reordering the tabs left the button still reading
 * "Next: Optimise Your Plan" on the Contributions screen while it navigated to the builder
 * (owner-caught immediately, 2026-08-27). A second source of the same sequence will always drift;
 * deriving prev/next by index cannot.
 */
const SECTION_FLOW: {
    id: SupportAtHomeSection;
    label: string;
    icon: typeof BookOpen;
    /**
     * This section draws its own Back/Next footer, so the page must not add a second one.
     *
     * The Service Builder's footer is contextual — it sits with "2 services selected" and the save
     * prompt — which is why it keeps its own rather than being flattened into the generic pair.
     * Before this flag the page rendered Previous/Next underneath it and the screen ended with two
     * Back buttons and two "Next: Optimise Your Plan" buttons (owner-caught, 2026-08-27).
     */
    ownsFooterNav?: true;
}[] = [
    { id: 'overview', label: 'Program Overview', icon: BookOpen },
    { id: 'contributions', label: 'Your Contributions', icon: Calculator },
    { id: 'builder', label: 'Service Builder', icon: Menu, ownsFooterNav: true },
    { id: 'strategies', label: 'Optimise Your Plan', icon: Lightbulb },
];

/** True when the section on screen supplies its own footer navigation. */
const sectionOwnsFooterNav = (id: SupportAtHomeSection): boolean =>
    SECTION_FLOW.some((s) => s.id === id && s.ownsFooterNav === true);

/** The section before/after `id` in SECTION_FLOW, or null at either end. */
const stepFrom = (id: SupportAtHomeSection, delta: 1 | -1): { id: SupportAtHomeSection; label: string } | null => {
    const at = SECTION_FLOW.findIndex((s) => s.id === id);
    return SECTION_FLOW[at + delta] ?? null;
};
type StrategyMode = 'manual' | 'suggestion' | '';

export interface SupportAtHomePageProps {
    setActivePage: (page: string) => void;
    factFind: DraftFactFind;
    setFactFind: React.Dispatch<React.SetStateAction<DraftFactFind>>;
    /**
     * Called with each generated Digital SOA (the strategy plus its printable snapshot) so it lands on
     * the server's advice register like aged care's (owner, 1 Oct 2026). App.tsx decides where.
     */
    onSoaGenerated?: (document: Record<string, unknown>) => void;
    /**
     * The app's saved plans — what the profile autosave sends to the server. Support at home's plans
     * are written to the session first (homeCareStrategyStorage); this carries them on to the file.
     */
    setSavedStrategies?: React.Dispatch<React.SetStateAction<Array<Record<string, unknown>>>>;
}

/**
 * THE SESSION'S SUPPORT AT HOME PLANS, CARRIED ONTO THE FILE (19 Oct 2026, caught live: a plan chosen
 * on Optimise was gone after a reload). The session copy of saved plans is rebuilt from the profile's
 * `savedStrategies` on load (profileApi MIRROR_KEYS_HELD_IN_APP_DATA), but Support at home only ever
 * wrote the session — the plans reached the profile only if Home was visited first and merged them in.
 * Every change now replaces the file's home_care records with the session's, so `chosen` moves too.
 */
export function syncHomeCarePlansToFile(prev: ReadonlyArray<Record<string, unknown>> | null | undefined): Array<Record<string, unknown>> {
    // An unreadable session never deletes what is on file.
    let session: unknown;
    try { session = JSON.parse(sessionStorage.getItem(HOME_CARE_STRATEGIES_KEY) || '[]'); } catch { return [...(prev || [])]; }
    if (!Array.isArray(session)) return [...(prev || [])];
    const homeCare = (session as Array<Record<string, unknown>>).filter((s) => s && s.type === 'home_care');
    return [...(prev || []).filter((s) => s?.type !== 'home_care'), ...homeCare];
}

const SupportAtHomePage: React.FC<SupportAtHomePageProps> = ({ setActivePage, factFind, setFactFind, onSoaGenerated, setSavedStrategies }) => {
    useEffect(() => {
        if (!setSavedStrategies) return undefined;
        const sync = () => setSavedStrategies((prev) => syncHomeCarePlansToFile(prev));
        window.addEventListener(HOME_CARE_STRATEGIES_UPDATED_EVENT, sync);
        return () => { window.removeEventListener(HOME_CARE_STRATEGIES_UPDATED_EVENT, sync); sync(); };
    }, [setSavedStrategies]);
    /*
     * WHEN SUPPORT SERVICES START, owned here because two sections need it: the Service Builder ASKS
     * (it is a property of the service plan) and Optimise READS (it is the date the Suggestion Engine
     * projects every candidate from). One input, one value — asking twice is how two screens come to
     * disagree about the same fact.
     */
    // Kept on the profile (goals.sahSupportStartDate) — it had been page state, lost on every reload.
    const supportStartDate = String((factFind?.goals as { sahSupportStartDate?: string } | undefined)?.sahSupportStartDate || '');
    const setSupportStartDate = (value: string) => setFactFind((prev) => ({ ...prev, goals: { ...(prev?.goals || {}), sahSupportStartDate: value } }));
    /*
     * THE CHOSEN PLAN, and the module opens on it (rebuild M2, 19 Oct 2026; aged care's hub rule of
     * 17 Oct): a family with a plan chosen lands on their plan, not on the guide.
     */
    const [chosenPlan, setChosenPlan] = useState<ChosenSahPlan | null>(() => chosenSahPlanFromSession());
    useEffect(() => {
        const refresh = () => setChosenPlan(chosenSahPlanFromSession());
        window.addEventListener(HOME_CARE_STRATEGIES_UPDATED_EVENT, refresh);
        return () => window.removeEventListener(HOME_CARE_STRATEGIES_UPDATED_EVENT, refresh);
    }, []);
    /*
     * "START A NEW MODEL" FROM HOME (owner, 20 Oct 2026, as aged care's on 19 Oct): step 1 with no
     * services, support starting now, and the setup questions asked again on step 3. The chosen plan
     * keeps its own priced services, so it is untouched until another plan is chosen.
     */
    const [startFresh] = useState(() => {
        try { return typeof window !== 'undefined' && !!sessionStorage.getItem(SAH_START_FRESH_KEY); } catch { return false; }
    });
    const [currentSection, setCurrentSection] = useState<SupportAtHomeSection>(() => (startFresh ? 'builder' : chosenSahPlanFromSession() ? 'hub' : 'builder'));
    const [visitedStep2, setVisitedStep2] = useState(false);

    /*
     * THE SERVICE PLAN, owned here for steps 1–3 (rebuild M3). The session's current plan (mirrored to
     * the profile) is the working copy, as before; every change is also written to the profile's
     * `servicePlan` with its category split, so the edit is saved by the autosave and the contribution
     * assessment prices the real categories rather than its 40/35/25 assumption.
     */
    const [sahPlan, setSahPlan] = useState<SahServicePlan>(() => {
        const p = getServicePlanWithSource().plan as { client?: Record<string, unknown>; partner?: Record<string, unknown> };
        return { client: (p?.client || {}) as SahServicePlan['client'], partner: (p?.partner || {}) as SahServicePlan['partner'] };
    });
    const changeSahPlan = (next: SahServicePlan) => {
        setSahPlan(next);
        setCurrentServicePlan(next as never);
        clearNoServicePlanChoice();
        setFactFind((prev) => ({ ...prev, servicePlan: { services: next, serviceDistribution: servicePlanDistribution(next as never), editedAt: new Date().toISOString() } as never }));
    };
    useEffect(() => {
        if (!startFresh) return;
        try { sessionStorage.removeItem(SAH_START_FRESH_KEY); } catch { /* storage unavailable */ }
        changeSahPlan({ client: {}, partner: {} });
        setFactFind((prev) => ({ ...prev, goals: { ...(prev?.goals || {}), sahSupportStartDate: '', sahSetupSeen: false } }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const sahYears = Number((factFind?.goals as { sahLegacyYears?: number } | undefined)?.sahLegacyYears) || 10;
    /** The saved plans the steps read (the chosen aged care plan for "Is X moving?"). */
    const [sessionSaved, setSessionSaved] = useState<SavedStrategy[]>(() => readSessionSaved());
    useEffect(() => {
        const refresh = () => setSessionSaved(readSessionSaved());
        window.addEventListener(HOME_CARE_STRATEGIES_UPDATED_EVENT, refresh);
        return () => window.removeEventListener(HOME_CARE_STRATEGIES_UPDATED_EVENT, refresh);
    }, []);
    useEffect(() => {
        if (currentSection === 'hub' && !chosenPlan) setCurrentSection('overview');
    }, [currentSection, chosenPlan]);
    // The tested plans ARE the Optimise step (owner, 19 Oct 2026, as in aged care on 17 Oct): there
    // is no mode chooser, and Manual Control is reached by "Build a plan yourself".
    const [strategyMode, setStrategyMode] = useState<StrategyMode>('suggestion');
    /* Reopening a saved plan remounts Optimise, which reads the plan to restore as it mounts (review,
       18 Oct 2026: "Reopen" reloaded the whole browser page to get the same effect). */
    const [strategiesMountKey, setStrategiesMountKey] = useState(0);
    
    // Clear any leftover restore strategy on mount if not explicitly reopening
    useEffect(() => {
        // Check if we're explicitly reopening a strategy from Dashboard
        const reopenStrategy = typeof window !== 'undefined' ? sessionStorage.getItem('careCompanionReopenHomeCareStrategy') : null;
        if (!reopenStrategy) {
            // No explicit reopen - clear any leftover restore strategy to prevent restoring unsaved strategies
            if (typeof window !== 'undefined') {
                const leftover = sessionStorage.getItem('careCompanionRestoreHomeCareStrategy');
                if (leftover) {
                    try {
                        const strategy = JSON.parse(leftover);
                        // Only clear if it's not a saved strategy (no id means it's leftover unsaved state)
                        if (!strategy.id) {
                            sessionStorage.removeItem('careCompanionRestoreHomeCareStrategy');
                        }
                    } catch {
                        // Clear corrupted data
                        sessionStorage.removeItem('careCompanionRestoreHomeCareStrategy');
                    }
                }
            }
        }
    }, []);
    
    // Check for reopened home care strategy
    useEffect(() => {
        try {
            const stored = typeof window !== 'undefined' ? sessionStorage.getItem('careCompanionReopenHomeCareStrategy') : null;
            if (stored) {
                const strategy = JSON.parse(stored);
                // Clear the stored strategy so it doesn't reopen again
                sessionStorage.removeItem('careCompanionReopenHomeCareStrategy');
                
                // Check if this is a service plan (has inputs.services) or a strategy from Optimise Your Plan
                // Service plans have inputs.services, strategies have inputs.gifting/funeralBonds
                // Priority: Check for service plan first (more specific), then strategy
                const hasServicePlan = strategy.inputs?.services !== undefined && 
                                      strategy.inputs?.services !== null &&
                                      Object.keys(strategy.inputs.services || {}).length > 0;
                const hasStrategyInputs = (strategy.inputs?.gifting !== undefined && strategy.inputs?.gifting !== null) || 
                                         (strategy.inputs?.funeralBonds !== undefined && strategy.inputs?.funeralBonds !== null);
                
                
                if (hasServicePlan && !hasStrategyInputs) {
                    // Navigate to Service Builder section to restore the service plan
                    setCurrentSection('builder');
                    // Store it for ServiceBuilderSection to restore
                    sessionStorage.setItem('careCompanionRestoreServicePlan', JSON.stringify(strategy));
                } else if (hasStrategyInputs) {
                    // Navigate to strategies section to restore strategy state
                    setCurrentSection('strategies');
                    // Store it for StrategiesSection to access
                    sessionStorage.setItem('careCompanionRestoreHomeCareStrategy', JSON.stringify(strategy));
                } else {
                    // Default to strategies if unclear (most home_care items are strategies from Optimise Your Plan)
                    setCurrentSection('strategies');
                    sessionStorage.setItem('careCompanionRestoreHomeCareStrategy', JSON.stringify(strategy));
                }
            }
        } catch (error) {
            console.error('Error loading reopened home care strategy:', error);
        }
    }, []);
    
    // Scroll to top when section changes
    useEffect(() => {
        scrollAppToTop();
    }, [currentSection]);
    
    // Check if this is a couple
    const isPartnered = factFind?.adviceFor === 'couple';
    
    // Get client's Support at Home level from ACAT assessment
    const getClientLevel = (): { label: string; budget: number; description: string } | null => {
        const clientAcat = factFind?.careNeeds?.clientAcat;
        if (clientAcat?.status === 'yes' && clientAcat?.homeCareLevel) {
            return ACAT_LEVEL_DATA[clientAcat.homeCareLevel] || null;
        }
        return null;
    };

    // Get partner's Support at Home level from ACAT assessment
    const getPartnerLevel = (): { label: string; budget: number; description: string } | null => {
        const partnerAcat = factFind?.careNeeds?.partnerAcat;
        if (partnerAcat?.status === 'yes' && partnerAcat?.homeCareLevel) {
            return ACAT_LEVEL_DATA[partnerAcat.homeCareLevel] || null;
        }
        return null;
    };

    const clientLevel = getClientLevel();
    const partnerLevel = getPartnerLevel();
    const [selectedLevel, setSelectedLevel] = useState<number | null>(clientLevel ? clientLevel.budget : null);
    const [selectedPartnerLevel, setSelectedPartnerLevel] = useState<number | null>(partnerLevel ? partnerLevel.budget : null);
    const [compareOpen, setCompareOpen] = useState(false);

    /*
     * A GUESS IS NOT A CLASSIFICATION (owner-caught, 2026-08-28).
     *
     * This screen used to greet an unassessed client with "we couldn't find your classification —
     * please select your recommended level", and the moment they picked one it appeared under
     * "Your Support at Home Classification" beside a green tick, with a budget to the cent. Every
     * contribution figure downstream was then calculated from a number the client had guessed off a
     * dropdown, and `clientLevel || { budget: selectedLevel }` handed it to ContributionsSection in
     * the same slot as a real assessment — so nothing downstream could tell the two apart.
     *
     * The exploration itself is worth keeping: someone waiting on an assessment reasonably wants to
     * know what a level would cost. What had to go is calling it their classification. `exploring`
     * is that distinction, and it now travels with the figure to every tab that shows one.
     */
    const clientExploring = !clientLevel && selectedLevel != null;
    const partnerExploring = !partnerLevel && selectedPartnerLevel != null;
    const exploring = clientExploring || partnerExploring;
    const clientName = factFind?.client?.firstName || 'You';
    const clientVerb = factFind?.client?.firstName ? 'hasn’t' : 'haven’t';

    const formatCurrency = (value: number | null | undefined): string => {
        return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value || 0);
    };

    /*
     * THE OPTIMISE SECTION IS THE ONLY TWO-PANE ONE, and it needs the benchmark's width to lay its
     * panes out the way Aged Care does: 1520px, giving a ~587px working pane and a ~391px plan
     * summary at the owner's 100% zoom (a ~1330px viewport). Capping it at 1152px squeezed both.
     *
     * The other sections stay at 1152px on purpose — they are single columns of reading, and prose
     * set 1520px wide is harder to read, not easier.
     */
    /** The plan page's links, each into the step that does it. */
    const openFromHub = (link: HubLink) => {
        const card = chosenPlan?.inputs?.card;
        if (link === 'services') { setCurrentSection('builder'); return; }
        // "A new model from scratch" on the plan page (owner, 22 Oct 2026): Home's start-fresh, from here —
        // step 1 with no services, support starting now, the setup questions asked again. The chosen plan
        // keeps its own priced services, so it stays chosen until another is.
        if (link === 'fresh') {
            changeSahPlan({ client: {}, partner: {} });
            setFactFind((prev) => ({ ...prev, goals: { ...(prev?.goals || {}), sahSupportStartDate: '', sahSetupSeen: false } }));
            setCurrentSection('builder');
            return;
        }
        if (link === 'guide') { setCurrentSection('overview'); return; }
        if (link === 'assumptions') { setActivePage('AssumptionsPage'); return; }
        if (link === 'plans') { setStrategyMode('suggestion'); setStrategiesMountKey((k) => k + 1); setCurrentSection('strategies'); return; }
        if (link === 'document' && chosenPlan?.soaSnapshot) { openSupportAtHomeDigitalSOAAttachment(chosenPlan.soaSnapshot as never); return; }
        // A plan built by hand is reopened with its own levers; a tested one is reached through the plans.
        const manual = card?.origin === 'manual';
        // "Build a plan yourself": a blank plan on the same services and household (25 Oct 2026).
        if (link === 'build-new') {
            setSahHubIntent({ kind: 'build-new' });
            setStrategyMode('manual');
            setStrategiesMountKey((k) => k + 1);
            setCurrentSection('strategies');
            return;
        }
        if (link === 'build' && !manual) setSahHubIntent({ kind: 'build', config: card?.engineConfig, name: String(chosenPlan?.name || '') });
        if (link === 'document') setSahHubIntent({ kind: 'document' });
        if (link === 'yearByYear') setSahHubIntent({ kind: 'yearByYear' });
        if (manual && chosenPlan) {
            try { sessionStorage.setItem('careCompanionRestoreHomeCareStrategy', JSON.stringify(chosenPlan)); } catch { /* storage unavailable */ }
        }
        setStrategyMode(manual || link === 'build' ? 'manual' : 'suggestion');
        setStrategiesMountKey((k) => k + 1);
        setCurrentSection('strategies');
    };

    if (currentSection === 'hub' && chosenPlan) {
        return (
            <SahHub
                factFind={factFind}
                plan={chosenPlan}
                currentServicePlan={(getServicePlanWithSource().plan as never) ?? null}
                supportStartDate={supportStartDate}
                savedStrategies={sessionSaved as never}
                onOpen={openFromHub}
                goHome={() => setActivePage('Dashboard')}
            />
        );
    }

    /*
     * THE THREE STEPS (rebuild M3, 19 Oct 2026; review decision 1, option B — aged care's spine). The four
     * tabs (overview, contributions, service builder, optimise) are now: 1 the support and its cost,
     * 2 what it means for them, 3 ways to improve their position. The guide is a link from step 1; the
     * official letter's rates (the contributions screen's override, kept for compliance) a link from step 2.
     */
    const stepOf = (s: SupportAtHomeSection): 1 | 2 | 3 | null => (s === 'builder' ? 1 : s === 'contributions' ? 2 : s === 'strategies' ? 3 : null);
    const step = stepOf(currentSection);
    const v = householdVoice(factFind);
    const pricedPlan = sahPricedServicePlan(sahPlan, factFind);
    const pricedServiceCount = (['client', 'partner'] as const).reduce((n, k) => n + Object.values(pricedPlan[k] || {}).filter((d) => d && Number((d as { quantity?: unknown }).quantity) > 0).length, 0);
    const pricedCount = (['client', 'partner'] as const).reduce((n, k) => n + Object.values(sahPlan[k] || {}).filter((d) => d && Number((d as { quantity?: unknown }).quantity) > 0).length, 0);
    const pricedValue = (['client', 'partner'] as const).reduce((t, k) => t + computeServiceCostsForPerson((sahPlan[k] || {}) as never, {}, SUPPORT_AT_HOME_SERVICES).total, 0);
    const steps = [
        { n: 1 as const, section: 'builder' as SupportAtHomeSection, title: 'The support and its cost', sub: pricedCount ? `${pricedCount} ${pricedCount === 1 ? 'service' : 'services'} · ${formatCurrency(pricedValue)} a year` : 'Who, when, which services', done: pricedCount > 0 },
        { n: 2 as const, section: 'contributions' as SupportAtHomeSection, title: `What it means for ${v.them}`, sub: 'Four numbers, and why', done: visitedStep2 },
        { n: 3 as const, section: 'strategies' as SupportAtHomeSection, title: `Ways to improve ${v.their} position`, sub: chosenPlan ? 'Plan chosen' : 'Plans we tested, or build your own', done: !!chosenPlan },
    ];
    const stepName = step ? steps[step - 1].title : currentSection === 'overview' ? 'The guide' : currentSection === 'letter' ? 'Rates from the letter' : '';

    return (
        <div className={currentSection === 'strategies' ? 'max-w-[1520px] mx-auto' : 'max-w-6xl mx-auto'} data-testid="sah-steps">
            <p className="text-[13px] text-slate-500">
                <button type="button" onClick={() => setActivePage('Dashboard')} className="underline decoration-dotted underline-offset-2 hover:text-slate-800">Home</button>
                {' › '}
                <button type="button" onClick={() => setCurrentSection(chosenPlan ? 'hub' : 'builder')} className="underline decoration-dotted underline-offset-2 hover:text-slate-800">Support at home</button>
                {stepName ? ` › ${stepName}` : ''}
            </p>
            <h1 className="mt-2 text-[32px] font-bold leading-tight tracking-tight text-brand-navy">Support at home for {v.names}</h1>
            {step === 1 && !chosenPlan && (
                <p className="mt-1 max-w-[72ch] text-[15px] text-slate-600">Three short steps: the support {v.they} need and what it costs, what that means for {v.them}, and the ways to improve {v.their} position.</p>
            )}

            {/* The steps — a row of three, each a door; done ones ticked. */}
            <nav className="mt-4 grid grid-cols-1 gap-2 md:grid-cols-3" aria-label="Steps" data-testid="sah-step-tiles">
                {steps.map((s) => {
                    const on = step === s.n;
                    return (
                        <button
                            key={s.n}
                            type="button"
                            onClick={() => { if (s.n === 2) setVisitedStep2(true); setCurrentSection(s.section); }}
                            aria-current={on ? 'step' : undefined}
                            className={`flex items-center gap-3 rounded-xl border bg-white px-4 py-3 text-left transition-colors ${on ? 'border-blue-600 ring-1 ring-blue-600' : s.done ? 'border-emerald-300 hover:border-emerald-500' : 'border-slate-200 hover:border-blue-300'}`}
                            data-testid={`sah-step-tile-${s.n}`}
                        >
                            <span className={`grid h-7 w-7 shrink-0 place-items-center rounded-full text-[13px] font-bold ${on ? 'bg-blue-600 text-white' : s.done ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-600'}`}>{!on && s.done ? '✓' : s.n}</span>
                            <span className="min-w-0">
                                <span className="block text-[14px] font-semibold text-slate-900">{s.title}</span>
                                <span className="block truncate text-[12.5px] text-slate-500">{s.sub}</span>
                            </span>
                        </button>
                    );
                })}
            </nav>

            <div className="mt-6">
                {currentSection === 'builder' && (
                    <SahStep1
                        factFind={factFind}
                        setFactFind={setFactFind}
                        plan={sahPlan}
                        onPlanChange={changeSahPlan}
                        supportStartDate={supportStartDate}
                        setSupportStartDate={setSupportStartDate}
                        years={sahYears}
                        savedStrategies={sessionSaved}
                        onReadGuide={() => setCurrentSection('overview')}
                        onNext={() => { setVisitedStep2(true); setCurrentSection('contributions'); }}
                        onOpenCashflow={() => setActivePage('CashflowPensionPage')}
                    />
                )}
                {currentSection === 'contributions' && (
                    <SahStep2
                        factFind={factFind}
                        servicePlan={sahPlan}
                        years={sahYears}
                        supportStartDate={supportStartDate}
                        savedStrategies={sessionSaved}
                        onBack={() => setCurrentSection('builder')}
                        onNext={() => { setStrategyMode('suggestion'); setCurrentSection('strategies'); }}
                        onEnterLetterRates={() => setCurrentSection('letter')}
                        onYearsChange={(y: number) => setFactFind((prev) => ({ ...prev, goals: { ...(prev?.goals || {}), sahLegacyYears: y } }))}
                    />
                )}
                {/* No services, nothing to price (decision 2: no illustrative package) — the plans wait for step 1. */}
                {currentSection === 'strategies' && pricedServiceCount === 0 && (
                    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card" data-testid="sah-step3-needs-services">
                        <h2 className="text-[19px] font-bold text-brand-navy">Choose the services first</h2>
                        <p className="mt-1 max-w-[72ch] text-[14px] leading-relaxed text-slate-700">
                            Every plan is priced on the support {v.names} need{v.s}: what it costs, and what {v.they} pay towards it. Add the services on step 1 and the ways to improve {v.their} position are tested on them.
                        </p>
                        <Button type="button" variant="brand" onClick={() => setCurrentSection('builder')} className="mt-4">Go to step 1 — the support and its cost</Button>
                    </div>
                )}
                {currentSection === 'strategies' && pricedServiceCount > 0 && (
                    <StrategiesSection
                        stepped
                        key={strategiesMountKey}
                        onReopen={() => { setStrategyMode('manual'); setStrategiesMountKey((k) => k + 1); }}
                        onSoaGenerated={onSoaGenerated}
                        factFind={factFind}
                        setFactFind={setFactFind}
                        onNext={() => setActivePage('Dashboard')}
                        onBack={() => setCurrentSection('contributions')}
                        strategyMode={strategyMode}
                        setStrategyMode={(mode: string) => setStrategyMode(mode as StrategyMode)}
                        onNavigateToBuilder={() => setCurrentSection('builder')}
                        onNavigateToDashboard={() => setActivePage('Dashboard')}
                        supportStartDate={supportStartDate}
                    />
                )}
                {/* THE GUIDE (from step 1): each person's classification, and for someone not yet assessed
                    the honest what-if and the entry-level fork (owner, 28 Aug 2026), then the guide itself. */}
                {currentSection === 'overview' && (
                    <button type="button" onClick={() => setCurrentSection('builder')} className="mb-4 text-[14px] font-semibold text-brand-navy underline decoration-slate-300 underline-offset-4">‹ Back to the support and its cost</button>
                )}
            {/* Not assessed: say so honestly, then offer the what-if as a what-if. */}
            {currentSection === 'overview' && !clientLevel && (
                <div className="space-y-4 mb-8">
                    <InsightCard tone="caution" heading={`${clientName} ${clientVerb} been assessed yet`}>
                        <p className="mb-3">
                            Support at Home costs depend on the level you&apos;re assessed at, so until that
                            happens we can&apos;t tell you what you&apos;d pay.{' '}
                            <strong>An assessment may also find that entry-level support (CHSP) meets your
                            needs</strong> — that program has no income or assets test, so there&apos;s nothing
                            for us to model.{' '}
                            <button
                                type="button"
                                onClick={() => setCompareOpen(true)}
                                className="font-semibold text-blue-600 underline decoration-blue-600/40 underline-offset-2 hover:text-blue-700"
                            >
                                How do the two compare?
                            </button>
                        </p>
                        <MyAgedCareApplyLink status="no" />
                    </InsightCard>

                    <InsightCard tone="neutral" heading="Want to see the numbers anyway? Try a level" icon={Sparkles}>
                        <p className="mb-3">
                            Pick a level to explore what it <em>would</em> cost. This is a what-if, not your
                            classification — we&apos;ll label it that way everywhere it appears.
                        </p>
                        <select
                            value={selectedLevel || ''}
                            onChange={(e) => setSelectedLevel(e.target.value ? parseFloat(e.target.value) : null)}
                            aria-label="Explore a Support at Home level"
                            className="w-full px-4 py-3 border border-slate-300 rounded-lg bg-white focus:ring-2 focus:ring-brand-blue focus:border-brand-blue"
                        >
                            <option value="">Choose a level to explore…</option>
                            {Object.entries(ACAT_LEVEL_DATA).filter(([, value]) => !value.transitional).map(([key, value]) => (
                                <option key={key} value={value.budget}>
                                    {value.label} - {formatCurrency(value.budget)} per year
                                </option>
                            ))}
                        </select>
                    </InsightCard>
                </div>
            )}

            <ChspComparisonModal open={compareOpen} onClose={() => setCompareOpen(false)} />

            {/* Classification Display - Only show on overview tab */}
            {currentSection === 'overview' && (clientLevel || selectedLevel || partnerLevel || selectedPartnerLevel) && (
                <div className="space-y-4 mb-8">
                    {/*
                      * The tile, the heading and the footnote all change with provenance. An assessed
                      * level keeps the tick and the definite heading; an explored one gets a question
                      * mark, a dashed edge, a conditional heading and an amber chip — so the reader
                      * cannot mistake one for the other at a glance, which is exactly what happened
                      * before (owner-caught, 2026-08-28).
                      */}
                    {(clientLevel || selectedLevel) && (
                    <div className={`bg-white/60 backdrop-blur-xl rounded-2xl shadow-lg shadow-gray-200/50 p-6 border ${clientLevel ? 'border-white' : 'border-dashed border-slate-300'}`}>
                        <div className="flex items-start gap-4">
                            <IconTile icon={clientLevel ? CheckCircle : HelpCircle} tone={clientLevel ? 'brand' : 'slate'} variant="solid" size="lg" />
                            <div className="flex-1">
                                <div className="flex flex-wrap items-center gap-2.5 mb-2">
                                    <h4 className="text-lg font-bold text-gray-800">
                                        {clientExploring
                                            ? `If ${isPartnered ? (factFind?.client?.firstName || 'they') : 'you'} ${isPartnered ? 'were' : 'were'} assessed at this level`
                                            : `${isPartnered ? `${factFind?.client?.firstName || 'Client'}'s` : 'Your'} Support at Home Classification`}
                                    </h4>
                                    {clientExploring && (
                                        <span className="rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-bold tracking-wide text-amber-800">
                                            EXPLORING — NOT ASSESSED
                                        </span>
                                    )}
                                </div>
                                <div className="flex flex-wrap items-center gap-3 mb-2">
                                    <span className="text-3xl font-bold text-brand-navy ">
                                        {clientLevel?.label || `Support at Home - Level ${Object.entries(ACAT_LEVEL_DATA).find(([_, v]) => v.budget === selectedLevel)?.[0]?.replace('sah_level_', '') || ''}`}
                                    </span>
                                    <span className="text-2xl font-bold text-gray-900">
                                        {formatCurrency(clientLevel?.budget || selectedLevel)}
                                    </span>
                                    <span className="text-sm text-gray-600">per year</span>
                                </div>
                                <p className="text-sm text-gray-600 mt-2">
                                    {clientExploring
                                        ? 'The budget at this level. The real level could be higher or lower — only an assessment decides it.'
                                        : 'This budget is paid quarterly and managed by Services Australia.'}
                                </p>
                            </div>
                        </div>
                    </div>

                    )}

                    {/* Partner Classification (if coupled) */}
                    {isPartnered && (
                        <div className={`bg-white/60 backdrop-blur-xl rounded-2xl shadow-lg shadow-gray-200/50 p-6 border ${partnerLevel ? 'border-white' : 'border-dashed border-slate-300'}`}>
                            <div className="flex items-start gap-4">
                                <IconTile icon={partnerLevel ? CheckCircle : HelpCircle} tone={partnerLevel ? 'brand' : 'slate'} variant="solid" size="lg" />
                                <div className="flex-1">
                                    <div className="flex flex-wrap items-center gap-2.5 mb-2">
                                        <h4 className="text-lg font-bold text-gray-800">
                                            {partnerExploring
                                                ? `If ${factFind?.partner?.firstName || 'they'} were assessed at this level`
                                                : partnerLevel
                                                    ? `${factFind?.partner?.firstName || 'Partner'}'s Support at Home Classification`
                                                    : `${factFind?.partner?.firstName || 'Your partner'} hasn’t been assessed yet`}
                                        </h4>
                                        {partnerExploring && (
                                            <span className="rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-bold tracking-wide text-amber-800">
                                                EXPLORING — NOT ASSESSED
                                            </span>
                                        )}
                                    </div>
                                    {partnerLevel || selectedPartnerLevel ? (
                                        <>
                                            <div className="flex flex-wrap items-center gap-3 mb-2">
                                                <span className="text-3xl font-bold text-brand-navy ">
                                                    {partnerLevel?.label || `Support at Home - Level ${Object.entries(ACAT_LEVEL_DATA).find(([_, v]) => v.budget === selectedPartnerLevel)?.[0]?.replace('sah_level_', '') || ''}`}
                                                </span>
                                                <span className="text-2xl font-bold text-gray-900">
                                                    {formatCurrency(partnerLevel?.budget || selectedPartnerLevel)}
                                                </span>
                                                <span className="text-sm text-gray-600">per year</span>
                                            </div>
                                            <p className="text-sm text-gray-600 mt-2">
                                                {partnerExploring
                                                    ? 'The budget at this level. The real level could be higher or lower — only an assessment decides it.'
                                                    : 'This budget is paid quarterly and managed by Services Australia.'}
                                            </p>
                                        </>
                                    ) : (
                                        <>
                                            <p className="text-sm text-amber-700 mb-2 flex items-center gap-1.5">
                                                <AlertCircle size={15} className="flex-shrink-0" />
                                                You can explore what a level would cost:
                                            </p>
                                            <select
                                                value={selectedPartnerLevel || ''}
                                                onChange={(e) => setSelectedPartnerLevel(e.target.value ? parseFloat(e.target.value) : null)}
                                                aria-label={`Explore a Support at Home level for ${factFind?.partner?.firstName || 'partner'}`}
                                                className="w-full px-4 py-3 border border-slate-300 rounded-lg bg-white focus:ring-2 focus:ring-brand-blue focus:border-brand-blue"
                                            >
                                                <option value="">Choose a level to explore…</option>
                                                {Object.entries(ACAT_LEVEL_DATA).map(([key, value]) => (
                                                    <option key={key} value={value.budget}>
                                                        {value.label} - {formatCurrency(value.budget)} per year
                                                    </option>
                                                ))}
                                            </select>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
                {currentSection === 'overview' && (
                    <ProgramOverviewSection
                        selectedLevel={selectedLevel}
                        clientLevel={clientLevel}
                        onNext={() => setCurrentSection('builder')}
                        awaitingAssessment={!clientLevel && !partnerLevel}
                        onNavigate={setActivePage}
                    />
                )}
                {currentSection === 'letter' && (
                    <>
                        <button type="button" onClick={() => setCurrentSection('contributions')} className="mb-4 text-[14px] font-semibold text-brand-navy underline decoration-slate-300 underline-offset-4">‹ Back to what it means for {v.them}</button>
                        {/* The label follows an explored level onto the one screen that prices it (owner, 28 Aug 2026). */}
                        {exploring && currentSection === 'letter' && (
                            <p className="mb-4 inline-flex rounded-full border border-amber-300 bg-white px-3 py-1 text-[12px] font-bold text-amber-800" data-testid="sah-letter-whatif">WHAT-IF — priced on a level being explored, not an assessment</p>
                        )}
                        <ContributionsSection factFind={factFind} setFactFind={setFactFind} clientLevel={clientLevel || { budget: selectedLevel }} onNext={() => setCurrentSection('contributions')} onBack={() => setCurrentSection('contributions')} />
                    </>
                )}
            </div>
        </div>
    );
};


export default SupportAtHomePage;
